import { TimeBucketOrder } from '@fabric-ui/components/Table';
import { defaultDrillDownHierarchy, DimensionTypes } from '../../views/CustomDashboards/constants';
import { filterTimeBucketFromDimensions, hasTimeBucket } from '../../views/CustomDashboards/EditCustomDashboard/TimeBucketBreakdown/utils';
import {
  AllTypeWidgets,
  DwQueryRequest,
  Field,
  PivotTableRequest,
  Select,
  SortBy,
  SortDir,
  TableSubType,
} from '../../views/CustomDashboards/types';
import { FieldType } from '../../views/CustomDashboards/types/types';

const addMetric = ({
  requests,
  fieldType,
  fields,
  pathAlias,
}: {
  requests: DwQueryRequest[];
  fieldType: FieldType;
  fields: string[];
  pathAlias?: string;
}) => [...requests[0].select[fieldType], ...fields.map(name => ({ name, ...(pathAlias ? { alias: pathAlias } : {}) }))];

const addDimensionInPivotTable = (requests: PivotTableRequest[], selectedFields: string[], fieldType: DimensionTypes | undefined) => {
  if (requests[0].select.dims.length === 0) {
    return selectedFields.map(field => ({ name: field, type: fieldType || DimensionTypes.Row }));
  }

  const isRowDimSelected = requests[0].select.dims.some(({ type }) => type === DimensionTypes.Row);
  const type = isRowDimSelected ? DimensionTypes.Col : DimensionTypes.Row;

  return selectedFields.map(field => ({ name: field, type }));
};
const getSelectedDims = (widget: AllTypeWidgets, selectedFields: string[], fieldType: DimensionTypes | undefined) => {
  if (widget.type === TableSubType.PivotTable) {
    return addDimensionInPivotTable(widget.requests, selectedFields, fieldType);
  }

  return selectedFields.map(field => ({ name: field }));
};

const addDimension = (
  widget: AllTypeWidgets,
  selectedFields: string[],
  isMultiTimeBreakdown: boolean,
  fieldType: DimensionTypes | undefined
) => {
  const fieldsObject = getSelectedDims(widget, selectedFields, fieldType);
  const allFieldsAreTimeBucket = selectedFields.every(field => hasTimeBucket(field));

  if (allFieldsAreTimeBucket && widget.requests[0].select.dims.find(dim => hasTimeBucket(dim.name)) && !isMultiTimeBreakdown) {
    return [...fieldsObject, ...filterTimeBucketFromDimensions(widget.requests[0].select.dims)];
  }

  if (allFieldsAreTimeBucket) {
    const selectedTimeBuckets = widget.requests[0].select.dims.filter(dim => hasTimeBucket(dim.name));
    return [...selectedTimeBuckets, ...fieldsObject, ...filterTimeBucketFromDimensions(widget.requests[0].select.dims)];
  }

  return [...widget.requests[0].select.dims, ...fieldsObject];
};

const getTimeBucketSortBasis = (dims: Field[]) => {
  const timeBuckets = dims.filter(({ name }) => hasTimeBucket(name));
  return timeBuckets.sort(({ name: name1 }, { name: name2 }) => TimeBucketOrder[name1] - TimeBucketOrder[name2]);
};

const getSortBasis = ({ widget, dims, metrics, historicalFFWithTimeBucketSelected }) => {
  const filteredDimensions = filterTimeBucketFromDimensions(dims);

  if (historicalFFWithTimeBucketSelected) {
    return getTimeBucketSortBasis(dims);
  }

  if (widget.type === TableSubType.PivotTable && !widget.definition.custom.showColTotals) {
    return filteredDimensions;
  }
  return metrics.length ? metrics : filteredDimensions;
};

const getSorting = ({
  widget,
  dims,
  metrics,
  sorting,
  isHistoricalAnalyticsFeatureFlag,
  isTimeBucketDimensionSelected,
  historicalFFWithTimeBucketSelected,
}) => {
  const updatedDims = new Set(dims.map(({ name }) => name));
  const updatedMetrics = new Set(metrics.map(({ name }) => name));
  const sortingColWithUpdatedDimsAndMetrics = sorting.filter(({ name }) => updatedDims.has(name) || updatedMetrics.has(name));

  const newSort = getSortBasis({ widget, dims, metrics, historicalFFWithTimeBucketSelected });

  return sortingColWithUpdatedDimsAndMetrics.length &&
    (!isHistoricalAnalyticsFeatureFlag || (isHistoricalAnalyticsFeatureFlag && !isTimeBucketDimensionSelected))
    ? sortingColWithUpdatedDimsAndMetrics
    : newSort[0]
      ? ([
          {
            name: newSort[0].alias || newSort[0].name,
            dir: historicalFFWithTimeBucketSelected ? SortDir.ASC : SortDir.DESC,
          },
        ] as SortBy[])
      : [];
};

const getNewRequestParams = ({
  widget,
  fields = [],
  pathAlias,
  fieldCategory,
  fieldType,
  isHistoricalAnalyticsFeatureFlag,
  isMultiTimeBreakdown = true,
}: {
  fieldCategory: FieldType;
  fieldType?: DimensionTypes;
  fields: string[];
  pathAlias?: string;
  widget: AllTypeWidgets;
  isHistoricalAnalyticsFeatureFlag: boolean;
  isMultiTimeBreakdown?: boolean;
}) => {
  const { requests } = widget;
  const fieldsSet = new Set(fields);
  const isFieldAlreadySelected = requests[0].select[fieldCategory].some(({ name }) => fieldsSet.has(name));

  const newSelect: Select = {
    ...requests[0].select,
    [fieldCategory]: isFieldAlreadySelected
      ? requests[0].select[fieldCategory].filter(({ name }) => !fieldsSet.has(name))
      : fieldCategory === FieldType.Measurements
        ? addMetric({ requests, fieldType: fieldCategory, fields, pathAlias })
        : addDimension(widget, fields, isMultiTimeBreakdown, fieldType),
  };

  const isTimeBucketDimensionSelected = !!newSelect.dims.find(dim => hasTimeBucket(dim.name));
  const historicalFFWithTimeBucketSelected = isHistoricalAnalyticsFeatureFlag && isTimeBucketDimensionSelected;

  const newDistinct = !newSelect.metrics.length && !!newSelect.dims.length;
  const newGroupBy = newSelect.metrics.length && newSelect.dims.length ? newSelect.dims.map(v => v.name) : [];

  const newSortBy = getSorting({
    widget,
    dims: widget.type === TableSubType.PivotTable ? newSelect.dims.filter(({ type }) => type === DimensionTypes.Row) : newSelect.dims,
    metrics: newSelect.metrics,
    sorting: requests[0].sorting,
    isHistoricalAnalyticsFeatureFlag,
    isTimeBucketDimensionSelected,
    historicalFFWithTimeBucketSelected,
  });

  const newSortingCol =
    widget.type === TableSubType.PivotTable
      ? getSorting({
          widget,
          dims: newSelect.dims.filter(({ type }) => type === DimensionTypes.Col),
          metrics: newSelect.metrics,
          sorting: widget.requests[0].sortingCol ?? [],
          isHistoricalAnalyticsFeatureFlag,
          isTimeBucketDimensionSelected,
          historicalFFWithTimeBucketSelected,
        })
      : undefined;

  const newDrillDownHierarchy =
    newSelect.dims.length !== 1 || newSelect.metrics.length !== 1 || historicalFFWithTimeBucketSelected
      ? defaultDrillDownHierarchy
      : widget.drillDownHierarchy;

  const newTop = newSelect.dims.length !== 1 ? { ...requests[0].top, groupOthers: false } : requests[0].top;

  return { newSelect, newGroupBy, newSortBy, newDistinct, newDrillDownHierarchy, newTop, newSortingCol };
};

export default getNewRequestParams;
